import { useEffect, useRef } from 'react';
import type { AudioEngine } from '@engine/audio';
import {
  BendTracker,
  GestureClassifier,
  PluckDetector,
  liveBend,
} from '@engine/gesture';
import {
  useFlashStore,
  useGestureStore,
  useHandStore,
  useSettingsStore,
  useSynthStore,
  useTransportStore,
  useTuningStore,
  useUiStore,
  deriveStrings,
  type HandId,
} from '@store/index';
import { computeStringYs } from '@engine/scale/layout';
import { getTransportSingletons } from './useTransport';

/**
 * Orchestrates per-frame gesture → audio pipeline.
 *
 * Pluck detection is ALWAYS active (not gated by classifier mode) —
 * the classifier only drives bend-anchor and mute events.
 *
 * Per hand, per frame:
 *   1. Classifier → current mode (idle/pluck/bend/mute)
 *   2. Mode transitions → start/stop bend anchor, cut voices on mute
 *   3. PluckDetector → emits events for thumb-up / index-down / middle-down
 *   4. In bend mode → BendTracker applies cents to voices of that hand
 */
/** Minimum time gap between two plucks on ADJACENT (±1) string indices
 *  WITHIN the same detection frame. Prevents cluster tone when several
 *  neighbouring strings cross in one frame (fast gliss) or a same-frame
 *  octave pluck lands on neighbours. Non-adjacent strings can fire at
 *  the same instant (bốc quản 8 an octave apart plays together). The
 *  gap is intentionally small (≈ frame duration / typical batch size)
 *  so a 3-4 string single-frame batch finishes inside its own frame
 *  window — larger values pile up across frames and make audio trail
 *  the visual hand by hundreds of ms on a long sweep. */
const ADJACENT_STRING_MIN_GAP_MS = 10;

/** How long after the classifier last saw a pluck-ready stance we keep
 *  admitting gliss events. A natural slow-to-medium gliss takes 400-800
 *  ms of continuous translation, during which the tips SUSTAINEDLY
 *  violate the pluck-ready criteria (curl deepens as the tip brushes
 *  across strings). 300 ms cut off mid-sweep around the 9th string.
 *  800 ms covers a full 17-string slow gliss while still expiring
 *  before a typical hand-lift-to-reposition (≥ 1 s of no plucking). */
const PLUCK_STANCE_GRACE_MS = 800;

/** Any admitted event extends the grace window — the sweep is provably
 *  in progress, so keep admitting. Guards against the case where the
 *  classifier drops to idle the WHOLE sweep (no pluck-stance reads at
 *  all during the gesture) and the base grace anchored on the pre-sweep
 *  pluck expires mid-stroke. 500 ms is long enough to bridge short pauses
 *  at the decelerating end of a slow sweep (tip slowing to cross the last
 *  few strings) — 250 ms was cutting those tail strings. */
const SWEEP_CONTINUATION_MS = 500;

/** How long after a THUMB pluck we treat that hand as "actively playing
 *  with the thumb" and suppress bend entry. Rationale: in đàn tranh, bend
 *  is done with the fingers while the thumb rests. If the thumb just
 *  plucked, the cupped-finger pose is bốc quản 8, not a bend. Between
 *  thumb strokes in a sustained bốc quản 8 run the gap is ~300–500 ms
 *  (FLICK_REFRACTORY_MS floor = 300), so 300 ms keeps bend suppressed
 *  during continuous play but lets bend arm naturally if the player
 *  clearly stops thumb-plucking. Non-thumb plucks never trip this. */
const BEND_BLOCK_AFTER_THUMB_MS = 300;

/** Palm-back heuristic. Uses TWO independent signals and requires both
 *  to agree — prevents false positives during play while still detecting
 *  palm-back in any finger orientation (fingers up OR down).
 *
 *  Signal A (palm-plane cross product): z-component of
 *    (indexMCP − wrist) × (pinkyMCP − wrist). Sign flips between
 *    palm-front and palm-back; parity depends on handedness.
 *
 *  Signal B (thumb-side test): on a palm-front hand the thumb is on the
 *    midline side (closer to the body); on palm-back the thumb flips to
 *    the other side. Robust because thumb-to-index-MCP distance is large
 *    and the axis flips cleanly regardless of finger posture.
 *
 *  Requiring agreement lets us drop the old "fingers above wrist" guard —
 *  so palm-back mutes during active play, not only when held above the
 *  wrist. */
function isPalmFacingBack(lm: readonly { x: number; y: number }[], hand: HandId): boolean {
  const w = lm[0]!;
  const i = lm[5]!;  // index MCP
  const p = lm[17]!; // pinky MCP
  const t = lm[2]!;  // thumb MCP

  // A: palm-plane cross product.
  const cross = (i.x - w.x) * (p.y - w.y) - (i.y - w.y) * (p.x - w.x);
  // Raised floor from 0.002 — near-zero cross is an edge-on hand, not a
  // confident signal. 0.006 kills most play-motion false positives.
  if (Math.abs(cross) < 0.006) return false;
  // Empirical parity (mirrored-camera view, MediaPipe labels passed
  // through unchanged): the hand in the 'right' HandId slot reports
  // cross > 0 on palm-back. Left mirrors. This matches the in-frame
  // geometry MediaPipe actually sees, not the user's physical hand.
  const crossSaysBack = hand === 'right' ? cross > 0 : cross < 0;
  if (!crossSaysBack) return false;

  // B: thumb-side test in the same frame geometry — the thumb MCP is
  //    on the viewer's right of index MCP (t.x > i.x) when palm-BACK
  //    for the 'right' HandId slot.
  const thumbSaysBack = hand === 'right' ? t.x > i.x : t.x < i.x;
  return thumbSaysBack;
}

/** Count extended fingers (0..5). Hand is assumed roughly vertical (as
 *  when signalling a number) — we test tip.y vs pip.y for index..pinky
 *  and thumb-tip distance from index MCP for the thumb. */
function countExtendedFingers(lm: readonly { x: number; y: number }[]): number {
  let n = 0;
  const pairs: [number, number][] = [
    [8, 6], // index
    [12, 10], // middle
    [16, 14], // ring
    [20, 18], // pinky
  ];
  for (const [tip, pip] of pairs) {
    if (lm[tip]!.y < lm[pip]!.y - 0.02) n++;
  }
  // Thumb: tip further from index-MCP than thumb-MCP is → extended outward.
  const thumbTip = lm[4]!;
  const thumbMcp = lm[2]!;
  const indexMcp = lm[5]!;
  const dTip = Math.hypot(thumbTip.x - indexMcp.x, thumbTip.y - indexMcp.y);
  const dMcp = Math.hypot(thumbMcp.x - indexMcp.x, thumbMcp.y - indexMcp.y);
  if (dTip > dMcp * 1.15) n++;
  return n;
}

export function useGestureLoop(engineRef: React.RefObject<AudioEngine | null>) {
  const hands: HandId[] = ['left', 'right'];
  const lastThumbPluckAtRef = {
    current: { left: -Infinity, right: -Infinity } as Record<HandId, number>,
  };
  const classifiersRef = useRef<Record<HandId, GestureClassifier>>({
    left: new GestureClassifier('left'),
    right: new GestureClassifier('right'),
  });
  const bendsRef = useRef<Record<HandId, BendTracker>>({
    left: new BendTracker({
      deadzone: useSettingsStore.getState().bendDeadzone,
      fullRange: useSettingsStore.getState().bendFullRange,
      maxCents: useSettingsStore.getState().bendMaxCents,
    }),
    right: new BendTracker({
      deadzone: useSettingsStore.getState().bendDeadzone,
      fullRange: useSettingsStore.getState().bendFullRange,
      maxCents: useSettingsStore.getState().bendMaxCents,
    }),
  });
  const plucksRef = useRef<Record<HandId, PluckDetector>>({
    // Symmetric sensitivity — both detectors read the same global
    // `pluckMinVelocity`. Per-hand asymmetry was removed per user
    // directive ("cho 2 tay sensitivity bằng nhau").
    left: new PluckDetector('left', {
      stringYs: [],
      minVelocity: useSettingsStore.getState().pluckMinVelocity,
      refractoryMs: 80,
    }),
    right: new PluckDetector('right', {
      stringYs: [],
      minVelocity: useSettingsStore.getState().pluckMinVelocity,
      refractoryMs: 80,
    }),
  });
  /** Last time the classifier reported a `pluck` stance, per hand.
   *  A short grace window (see PLUCK_STANCE_GRACE_MS) admits gliss
   *  events even when the classifier blips to `idle` mid-sweep —
   *  rapid tip motion can momentarily break the pluck-ready criteria
   *  (tip spread, y-spread), which otherwise severs a long gliss
   *  after 8-9 strings. */
  const pluckStanceAtRef = useRef<Record<HandId, number>>({
    left: -Infinity,
    right: -Infinity,
  });
  /** Last time ANY event from the detector was admitted by the filter.
   *  Used by the sweep-continuation rule to keep a long gliss flowing
   *  even when the base pluck-stance grace has expired. */
  const lastAdmittedAtRef = useRef<Record<HandId, number>>({
    left: -Infinity,
    right: -Infinity,
  });

  useEffect(() => {
    const unsub = useSettingsStore.subscribe((s) => {
      for (const h of hands) {
        classifiersRef.current[h].updateSettings({
          modeStickinessMs: s.modeStickinessMs,
          bendAlignThreshold: s.bendAlignThreshold,
          bendExtendedThreshold: s.bendExtendedThreshold,
        });
        bendsRef.current[h].updateSettings({
          deadzone: s.bendDeadzone,
          fullRange: s.bendFullRange,
          maxCents: s.bendMaxCents,
        });
        plucksRef.current[h].updateSettings({
          minVelocity: s.pluckMinVelocity,
        });
      }
    });
    return unsub;
  }, []);

  useEffect(() => {
    // Seed + re-sync the pluck detector's string-Y array whenever the
    // tuning (pitches, count) or the layout mode changes. The detector
    // never computes layout itself — it just tests Y crossings against
    // whatever positions we feed it.
    const syncStringYs = () => {
      const t = useTuningStore.getState();
      const active = t.sets[t.activeIndex];
      if (!active) return;
      const u = useUiStore.getState();
      const derived = deriveStrings(active);
      const ys = computeStringYs(active.stringCount, {
        spacing: u.stringSpacing,
        orderLowTop: u.stringOrderLowTop,
        pitches: derived.map((d) => d.midi + d.cents / 100),
      });
      for (const h of hands) {
        plucksRef.current[h].updateSettings({ stringYs: ys });
      }
    };
    syncStringYs();
    // Gate tuning subscription to fields that actually change the Y
    // layout. Without this, every preview flash or bend-anchor update
    // would trigger a re-derive + recompute, burning CPU for no reason.
    const unsubT = useTuningStore.subscribe((s, prev) => {
      if (s.activeIndex !== prev.activeIndex) return syncStringYs();
      const a = s.sets[s.activeIndex];
      const b = prev.sets[prev.activeIndex];
      if (!a || !b) return syncStringYs();
      if (
        a.stringCount !== b.stringCount ||
        a.scaleSize !== b.scaleSize ||
        a.scalePeriod !== b.scalePeriod ||
        a.bassPitchClass !== b.bassPitchClass ||
        a.bassOctave !== b.bassOctave ||
        a.bassCents !== b.bassCents ||
        a.lowestDegreeIdx !== b.lowestDegreeIdx ||
        a.scaleNotes !== b.scaleNotes ||
        a.overrides !== b.overrides
      ) {
        syncStringYs();
      }
    });
    const unsubU = useUiStore.subscribe((s, prev) => {
      if (s.stringSpacing !== prev.stringSpacing) return syncStringYs();
      if (s.stringOrderLowTop !== prev.stringOrderLowTop) return syncStringYs();
    });
    return () => {
      unsubT();
      unsubU();
    };
  }, []);

  /** Gesture: show the BACK OF ONE HAND to the camera, with N fingers
   *  extended, and hold steady for POSE_HOLD_MS. Left hand picks the
   *  scale set (set N); right hand picks the timbre preset (timbre N).
   *  1 finger = thumb only → preset index 0, 2 fingers = index 1, etc.
   *  Palm-front (normal orientation) doesn't fire — keeps playing poses
   *  safe. */
  const POSE_HOLD_MS = 450;
  const POSE_COOLDOWN_MS = 1200;
  const poseRef: Record<HandId, { count: number; startedAt: number; lastFiredAt: number }> = {
    left: { count: 0, startedAt: 0, lastFiredAt: -Infinity },
    right: { count: 0, startedAt: 0, lastFiredAt: -Infinity },
  };
  /** Rising-edge tracker for palm-back mute: when the player first
   *  enters command pose, hard-cut every ringing voice so the ear
   *  registers "mode switch" instantly. */
  const prevAnyPalmBackRef = { v: false };

  const step = (now: number): void => {
    const engine = engineRef.current;
    if (!engine || !engine.isReady()) return;

    const handsState = useHandStore.getState();
    const gestureState = useGestureStore.getState();
    const tuningState = useTuningStore.getState();
    const activeSet = tuningState.sets[tuningState.activeIndex]!;
    const flash = useFlashStore.getState().flash;

    // Adjacency-scheduler state — BATCH LOCAL (one step() call = one
    // detection frame). Shared across both hands this frame so a
    // cross-hand neighbour stagger still works, but reset frame-to-frame
    // so a long sweep doesn't accumulate lag across frames. Cross-frame
    // clustering protection comes from the detector's own per-string
    // refractory (PER_STRING_REFRACTORY_MS) plus the next frame starting
    // ≥ 30 ms after the current, which already exceeds the adjacency gap.
    const batchStringFireTargets: Record<number, number> = {};

    // Track palm-back pose per hand. If EITHER hand is signalling,
    // mute both: the player is intentionally in command mode, nothing
    // should play until they drop the pose.
    const palmBack: Record<HandId, boolean> = { left: false, right: false };
    for (const h of ['left', 'right'] as const) {
      const hand = handsState[h];
      if (hand.present && hand.landmarks) {
        palmBack[h] = isPalmFacingBack(hand.landmarks, h);
      }
    }
    const anyPalmBack = palmBack.left || palmBack.right;
    if (anyPalmBack && !prevAnyPalmBackRef.v) {
      engine.muteAll();
    }
    prevAnyPalmBackRef.v = anyPalmBack;

    // Palm-back + finger-count gesture dispatch.
    for (const h of ['left', 'right'] as const) {
      const hand = handsState[h];
      const ref = poseRef[h];
      if (!hand.present || !hand.landmarks) {
        ref.startedAt = 0;
        ref.count = 0;
        continue;
      }
      const lm = hand.landmarks;
      const back = isPalmFacingBack(lm, h);
      const n = back ? countExtendedFingers(lm) : 0;
      if (!back || n < 1) {
        ref.startedAt = 0;
        ref.count = 0;
        continue;
      }
      if (ref.count !== n) {
        ref.count = n;
        ref.startedAt = now;
        continue;
      }
      if (
        now - ref.startedAt >= POSE_HOLD_MS &&
        now - ref.lastFiredAt >= POSE_COOLDOWN_MS
      ) {
        if (h === 'left') {
          const sets = useTuningStore.getState().sets;
          const idx = Math.max(0, Math.min(sets.length - 1, n - 1));
          useTuningStore.getState().setActive(idx);
        } else {
          const synth = useSynthStore.getState();
          const idx = Math.max(0, Math.min(synth.slots.length - 1, n - 1));
          synth.setActiveSlot(idx);
        }
        ref.lastFiredAt = now;
        ref.startedAt = now + POSE_COOLDOWN_MS;
      }
    }

    const tuning = deriveStrings(activeSet);

    for (const h of hands) {
      const snap = handsState[h];
      const cls = classifiersRef.current[h];
      const bend = bendsRef.current[h];
      const pluck = plucksRef.current[h];
      const prevMode = gestureState[h].mode;

      const { mode: classifierMode } = cls.classify({ landmarks: snap.landmarks, now });
      if (classifierMode === 'pluck') {
        pluckStanceAtRef.current[h] = now;
      }
      const inPluckStance =
        now - pluckStanceAtRef.current[h] <= PLUCK_STANCE_GRACE_MS;
      const sweepContinuing =
        now - lastAdmittedAtRef.current[h] <= SWEEP_CONTINUATION_MS;
      const admitGliss = inPluckStance || sweepContinuing;

      // Motion-based events (pluck + gliss) run every frame the hand has
      // landmarks. Suppress while:
      //   · either hand is in palm-back command pose, OR
      //   · THIS hand's classifier is currently in `bend` mode — motion
      //     events during bend would both trigger mode-override to pluck
      //     and fire phantom gliss plucks on top of the bent note.
      // We now trust the classifier fully: with the tighter
      // LOWER_BELOW_UPPER_MIN = 0.02 threshold, `bend` is only reported
      // for genuine cupped posture. Gliss-induced transient curls no
      // longer satisfy the bend layer test, so we don't need a
      // sweep-continuation override here.
      let motionEvents: ReturnType<typeof pluck.step> = [];
      if (snap.landmarks && !anyPalmBack && classifierMode !== 'bend') {
        motionEvents = pluck.step(snap.landmarks, now);
      }

      // Track the thumb as a bend-inhibitor for bốc quản 8 (thumb pluck
      // + finger pluck on strings an octave apart). A cupped finger pose
      // with active thumb plucking is NOT a bend, even though posture
      // matches the bend layer tests.
      if (motionEvents.some((e) => e.finger === 'thumb')) {
        lastThumbPluckAtRef.current[h] = now;
      }
      const thumbRecentlyPlucked =
        now - lastThumbPluckAtRef.current[h] < BEND_BLOCK_AFTER_THUMB_MS;

      // Mode is what the classifier says, with a single exit-path override:
      // if we WERE bending and motion events just arrived, the user has
      // left bend posture to strike — commit to pluck so bend releases.
      // We no longer override when `classifierMode === 'bend'` — that was
      // preventing a genuine bend from ever engaging because any motion
      // during the bend-entry transition would force mode back to pluck.
      // With the tighter bend threshold the classifier won't say `bend`
      // unless posture is truly cupped, so we trust it.
      let mode = classifierMode;
      if (anyPalmBack) {
        mode = 'idle';
      } else if (motionEvents.length > 0 && prevMode === 'bend') {
        mode = 'pluck';
      } else if (thumbRecentlyPlucked && classifierMode === 'bend' && prevMode !== 'bend') {
        mode = 'idle';
      }

      // --- Mode transitions ---
      if (mode !== prevMode) {
        gestureState.setMode(h, mode, now);
        if (prevMode === 'bend') {
          bend.end();
          engine.releaseBend(h);
          gestureState.setBendAnchor(h, null);
          liveBend[h] = 0;
        }
        if (mode === 'bend' && snap.landmarks) {
          const anchor = bend.start(snap.landmarks);
          gestureState.setBendAnchor(h, anchor);
          // Keep ringing voices on this hand audible while the bend is
          // active — otherwise a bend on a half-decayed voice is silent.
          engine.engageBend(h);
        }
      }

      // --- Fire motion events ---
      // Single-event batches (plucks) fire immediately. Multi-event
      // batches (gliss sweep that crossed N strings in one frame) are
      // staggered so the listener hears distinct notes instead of a
      // simultaneous cluster. Envelope params are captured HERE (not
      // inside the flash store) so the visual decay tracks the exact
      // timbre that will play this note, even if the user swaps tones
      // before it dies.
      const synth = useSynthStore.getState();
      const slot = synth.slots[synth.activeSlotIndex]!;
      const env = {
        attackMs: slot.attackMs,
        decayMs: slot.decayMs,
        sustainFrac: slot.sustainFrac,
        tailSec: slot.tailSec,
      };
      // Gliss events (multi-string sweeps) only count when the hand is
      // actually in the ready-to-pluck stance. Lifting the hand to
      // reposition translates the wrist upward across multiple string
      // lines — without this gate, the thumb-gliss path fires a
      // cascade of phantom upward notes for every string crossed.
      // Admission uses two rules OR'd together:
      //   · pluck-stance grace (800 ms from last real pluck read) —
      //     catches the common case of plucking then sweeping.
      //   · sweep-continuation (250 ms from last admitted event) —
      //     keeps a long sweep flowing even if the classifier stayed
      //     idle the whole time, because the sweep itself is evidence.
      const firableEvents = motionEvents.filter(
        (e) => e.type === 'pluck' || admitGliss,
      );
      if (firableEvents.length > 0) {
        lastAdmittedAtRef.current[h] = now;
      }
      // Determine the PHYSICAL hand from wrist x (un-mirrored landmark
      // space: low x = camera's left = user's physical right). Used for
      // glow color so MediaPipe's handedness-label flicker can't make
      // the salmon right-hand glow flip to sky-blue. Pairwise compare
      // when both hands are present so a single-hand threshold isn't
      // needed. Falls back to the store HandId if landmarks absent.
      const otherSnap = handsState[h === 'left' ? 'right' : 'left'];
      let physicalHand: HandId = h;
      if (snap.landmarks) {
        const myX = snap.landmarks[0]!.x;
        if (otherSnap.present && otherSnap.landmarks) {
          physicalHand = myX < otherSnap.landmarks[0]!.x ? 'right' : 'left';
        } else {
          physicalHand = myX < 0.5 ? 'right' : 'left';
        }
      }
      // Quantize — Air Keyboard / Air Drum Set pattern.
      //
      // The intent moment is captured as `now` (performance.now()) by
      // the detection step. Translate that to AudioContext-time via the
      // perf→audio lag, snap to the nearest grid line (in audio time),
      // apply strength as a partial pull, and if the snapped time is
      // already in the past bump forward by full grid divisions until
      // it's a tiny safety margin ahead of ctxNow. Every voice is then
      // started at that exact audio time via PluckVoice's start(at),
      // sample-accurate against the metronome's scheduled clicks.
      //
      // For a multi-event GLISS batch we subdivide ONE grid cell evenly
      // across all events anchored on the snapped beat — that's how
      // "khi gliss thì subdivision khớp" turns into a tight even rhythm
      // locked to the bar.
      const tr = useTransportStore.getState();
      const transportSingleton = getTransportSingletons().transport;
      const quantizeOn = tr.quantizeOn && transportSingleton !== null;

      let ctxNow = 0;
      let intentAudio = 0;
      let snappedAudio = 0;
      let divSec = 0;
      if (transportSingleton) {
        ctxNow = transportSingleton.audioTime();
        // Detection batch's intent time = `now` (perf-time). Subtract the
        // perf-to-audio lag (how much wall-clock has elapsed since the
        // batch was timestamped) to get the equivalent audio-time.
        const lagMs = Math.max(0, performance.now() - now);
        intentAudio = ctxNow - lagMs / 1000;
        if (quantizeOn) {
          const nearest = transportSingleton.quantizeNearest(
            intentAudio,
            tr.quantGrid,
          );
          // Partial-strength snap: 0 = no pull (off), 1 = full snap.
          let target =
            intentAudio + (nearest - intentAudio) * tr.quantizeStrength;
          divSec = (tr.quantGrid * 60) / transportSingleton.bpm;
          // 5 ms safety margin — Web Audio scheduling needs the target
          // ≥ ctxNow to actually take effect. If snap put us in the past
          // (which is normal — half of all moments snap backward), bump
          // forward by whole grid cells until we're future. This keeps
          // the offset INSIDE the grid (0..gridSec) so the player still
          // hears the same beat position, just one cell later.
          const minTime = ctxNow + 0.005;
          while (target < minTime) target += divSec;
          snappedAudio = target;
        }
      }
      // Gliss subdivision — every event in a batch where at least one is
      // tagged `type: 'gliss'` shares an evenly-divided grid cell. With
      // 5 events at 1/16 grid we get 5 notes evenly spaced across one
      // 1/16-cell duration (each ~1/80 of a bar apart). For non-gliss
      // batches we fall back to the adjacency stagger (10 ms gap on
      // ±1 strings) or, when quantize is off, fire immediately.
      const isGlissBatch =
        firableEvents.length >= 2 &&
        firableEvents.some((e) => e.type === 'gliss');
      const glissSlotSec =
        isGlissBatch && quantizeOn && divSec > 0
          ? divSec / firableEvents.length
          : 0;

      for (let evtIdx = 0; evtIdx < firableEvents.length; evtIdx++) {
        const evt = firableEvents[evtIdx]!;
        const tun = tuning[evt.stringIndex];
        if (!tun) continue;
        const sIdx = evt.stringIndex;
        const freq = tun.freq;
        const vel = evt.velocity;
        // Audio routing uses the STORE HandId `h` so bend tracking and
        // voice-bucketing stay keyed to whatever MediaPipe assigned.
        const hand = h;
        // Flash color uses the PHYSICAL hand so the glow matches the
        // skeleton regardless of handedness-label flicker.
        const glowHand = physicalHand;

        // --- Compute startAt (audio-time) for this event ---
        let startAt: number | undefined;
        if (quantizeOn && transportSingleton) {
          if (glissSlotSec > 0) {
            // Subdivide the snapped cell evenly. Emission order ==
            // sweep order, so slot N matches the Nth string crossed.
            startAt = snappedAudio + evtIdx * glissSlotSec;
          } else {
            // Single pluck / chord — anchor every voice at the snapped
            // beat, then add the adjacency stagger so accidental ±1
            // cluster still gets the 10 ms gap. Non-adjacent chord
            // strings keep firing exactly together.
            let stagger = 0;
            for (const ns of [sIdx - 1, sIdx, sIdx + 1]) {
              const t = batchStringFireTargets[ns];
              if (t !== undefined) {
                stagger = Math.max(stagger, t + ADJACENT_STRING_MIN_GAP_MS);
              }
            }
            batchStringFireTargets[sIdx] = stagger;
            startAt = snappedAudio + stagger / 1000;
          }
        } else {
          // Quantize off — keep the legacy adjacency-stagger path so a
          // multi-string sweep batch still sounds cleanly staggered.
          let target = now;
          for (const ns of [sIdx - 1, sIdx, sIdx + 1]) {
            const t = batchStringFireTargets[ns];
            if (t !== undefined) {
              target = Math.max(target, t + ADJACENT_STRING_MIN_GAP_MS);
            }
          }
          batchStringFireTargets[sIdx] = target;
          if (target > now + 0.5 && transportSingleton) {
            startAt = ctxNow + (target - now) / 1000;
          }
        }

        // Audio: scheduled at the exact audio-time when supported. Visual
        // flash: schedule via setTimeout to match the audio (perf-time
        // window equal to the audio offset from ctxNow) so the glow
        // pulses on the same beat the ear hears.
        const audioDelayMs =
          startAt !== undefined ? Math.max(0, (startAt - ctxNow) * 1000) : 0;
        if (quantizeOn) {
          // Surface the snap delay so the panel can show the player
          // it's actually working. Only emitted for the FIRST event in
          // the batch — multi-event batches all share snappedAudio.
          if (evtIdx === 0) {
            useTransportStore.getState().reportQuantShift(audioDelayMs);
          }
        }
        if (audioDelayMs <= 0.5) {
          engine.pluck(sIdx, freq, vel, hand, startAt);
          flash(sIdx, vel, now, glowHand, env);
        } else {
          // Pass startAt straight to engine.pluck — Web Audio honours it
          // sample-accurately, so we don't need setTimeout for AUDIO.
          // The setTimeout is only for the VISUAL flash so it pulses
          // when the ear actually hears the note.
          engine.pluck(sIdx, freq, vel, hand, startAt);
          setTimeout(() => {
            flash(sIdx, vel, performance.now(), glowHand, env);
          }, audioDelayMs);
        }
      }

      // --- Bend pitch update ---
      if (mode === 'bend' && snap.landmarks) {
        const cents = bend.read(snap.landmarks);
        liveBend[h] = cents;
        engine.bend(h, cents);
      }
    }
  };

  return { step };
}
